// 256×1 palette LUT filter for indexed sprites (Fase 2).

import { Filter, GlProgram, Texture } from 'pixi.js';
import { physPal, consumePalDirty } from '../palette';

const vertex = `
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void) {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`;

const fragment = `
in vec2 vTextureCoord;
uniform sampler2D uTexture;
uniform sampler2D uPalette;
out vec4 finalColor;

void main(void) {
    vec4 sample = texture(uTexture, vTextureCoord);
    float idx = sample.r;
    vec3 rgb = texture(uPalette, vec2(idx, 0.5)).rgb;
    finalColor = vec4(rgb, sample.a);
}
`;

let paletteTex: Texture | null = null;
let paletteCanvas: HTMLCanvasElement | null = null;
let paletteCtx: CanvasRenderingContext2D | null = null;
let filter: Filter | null = null;
const palBytes = new Uint8ClampedArray(256 * 4);

function rebuildPaletteTexture(): void {
  for (let i = 0; i < 256; i++) {
    const v = i * 3;
    const p = i * 4;
    palBytes[p] = physPal[v];
    palBytes[p + 1] = physPal[v + 1];
    palBytes[p + 2] = physPal[v + 2];
    palBytes[p + 3] = 255;
  }
  if (!paletteCanvas) {
    paletteCanvas = document.createElement('canvas');
    paletteCanvas.width = 256;
    paletteCanvas.height = 1;
    paletteCtx = paletteCanvas.getContext('2d')!;
    paletteTex = Texture.from(paletteCanvas);
    paletteTex.source.scaleMode = 'nearest';
  }
  const img = paletteCtx!.createImageData(256, 1);
  img.data.set(palBytes);
  paletteCtx!.putImageData(img, 0, 0);
  paletteTex!.source.update();
}

export function getPaletteFilter(): Filter {
  if (!filter) {
    rebuildPaletteTexture();
    filter = new Filter({
      glProgram: GlProgram.from({ vertex, fragment }),
      resources: {
        uPalette: paletteTex!.source,
      },
    });
  }
  return filter;
}

export function syncPaletteFilter(): void {
  if (consumePalDirty() || !paletteTex) {
    rebuildPaletteTexture();
    if (filter) {
      filter.resources.uPalette = paletteTex!.source;
    }
  }
}

export function destroyPaletteFilter(): void {
  filter?.destroy();
  filter = null;
  paletteTex?.destroy(true);
  paletteTex = null;
  paletteCanvas = null;
  paletteCtx = null;
}
